import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  USER_APPROVE_STATUS,
  USER_LOAN_ESTIMATION_TYPE,
  USER_LOAN_PROGRAM,
  USER_OCCUPANCY,
  USER_QUALIFY_STATUS,
  USER_ROLE,
  userApproveStatuses,
  userLoanEstimationTypeMap,
  userLoanEstimationTypes,
  userLoanProgramMap,
  userLoanPrograms,
  userOccupancyMap,
  userOccupancys,
} from '@/config/constants';
import {
  useGetUsersByAdminQuery,
  useUpdateUserByAdminMutation,
} from '@/apis/extended/admin/userApi';
import { formatPrice } from '@/utils/helper';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const pipelineSteps = [
  { label: 'Leads', key: 'leads' },
  { label: 'Engaged', key: 'engaged' },
  { label: 'Budget Completed', key: 'budget' },
  { label: 'Application', key: 'application' },
  { label: 'Documents', key: 'documents' },
  { label: 'Pre-Approval & Shopping', key: 'pre_approval_shopping' },
  { label: 'Loan Estimate Review', key: 'loan_estimate' },
];

export const AdminUser = () => {
  const theme = useTheme();

  const pipelineTablePaperSx = {
    bgcolor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greyShades.border1}`,
    borderRadius: 2,
    boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
    overflow: 'hidden' as const,
  };

  const tableHeadRowSx = {
    bgcolor: alpha(theme.palette.primary.main, 0.07),
    '& .MuiTableCell-head': {
      borderBottom: `1px solid ${theme.palette.greyShades.border1}`,
    },
  };

  const outlinedControlSx = {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.greyShades.border1,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.text.primary, 0.22),
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  };

  // All hooks must be called at the top level
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userDetailDialog, setUserDetailDialog] = useState(false);

  // Use real API call with fallback to mock data if needed
  const {
    data: usersData,
    isLoading,
    error,
  } = useGetUsersByAdminQuery({
    offset: 0,
    limit: 100,
  });
  const [updateUserByAdmin] = useUpdateUserByAdminMutation();

  // Fallback to mock data if API fails or no data
  const users = usersData?.users || [];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case USER_APPROVE_STATUS.APPROVED:
      case USER_QUALIFY_STATUS.QUALIFY:
        return 'success';
      case USER_APPROVE_STATUS.PENDING:
      case USER_QUALIFY_STATUS.INITIAL:
        return 'warning';
      case USER_APPROVE_STATUS.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getPipelineStep = (user: any) => {
    if (!user) return 0;

    const propertyCount = Number(user.propertyCount || 0);
    const pendingDocs = Number(user.pendingDocumentRequestCount || 0);
    const contractStartedCount = Number(user.contractStartedCount || 0);

    if (user.manualPipelineStage) {
      const manualIndex = pipelineSteps.findIndex(
        (step) => step.key === user.manualPipelineStage
      );
      if (manualIndex !== -1) return manualIndex;
    }

    if (propertyCount === 0) return 0; // Leads

    if (user.qualifyStatus !== USER_QUALIFY_STATUS.QUALIFY) return 1; // Engaged

    if (user.approveStatus === USER_APPROVE_STATUS.NOT_SUBMITTED) return 2; // Budget Completed

    if (user.approveStatus === USER_APPROVE_STATUS.PENDING) return 3; // Application

    if (pendingDocs > 0) return 4; // Documents

    if (user.approveStatus === USER_APPROVE_STATUS.APPROVED) {
      return contractStartedCount > 0 ? 6 : 5; // Loan Estimate Review vs Pre-Approval
    }

    return 1;
  };

  const filteredUsers =
    users
      ?.filter((user: any) => user.role === USER_ROLE.BUYER)
      .filter(
        (user: any) =>
          user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [];

  const manualTabIndex = pipelineSteps.length;

  const getUsersByPipelineStep = (step: number) => {
    return filteredUsers.filter((user: any) => getPipelineStep(user) === step);
  };

  const handleUserClick = (user: any) => {
    setSelectedUser(user);
    setUserDetailDialog(true);
  };

  const handleManualStageChange = async (userId: string, nextStage: string) => {
    try {
      await updateUserByAdmin({
        id: userId,
        manualPipelineStage: nextStage || null,
      }).unwrap();
    } catch (err) {
      console.error('Failed to update manual pipeline stage', err);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{ color: theme.palette.text.primary, mb: 2 }}
        >
          Loading users...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  // Show error if API call fails
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load users. Please try again later.
        </Alert>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          Error:{' '}
          {typeof error === 'object' && error !== null && 'message' in error
            ? error.message
            : 'Unknown error'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 700,
            mb: 2,
          }}
        >
          User Pipeline Management
        </Typography>

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <i
                  className="fa-light fa-search"
                  style={{ color: theme.palette.text.secondary }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 420,
            '& .MuiOutlinedInput-root': {
              color: theme.palette.text.primary,
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
            },
            ...outlinedControlSx,
          }}
        />
      </Box>

      {/* Pipeline Tabs */}
      <Box
        sx={{ borderBottom: 1, borderColor: theme.palette.greyShades.border1 }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: theme.palette.text.secondary,
              fontWeight: 500,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          {pipelineSteps.map((step, index) => (
            <Tab
              key={step.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {step.label}
                  <Chip
                    label={getUsersByPipelineStep(index).length}
                    size="small"
                    color="primary"
                  />
                </Box>
              }
            />
          ))}
          <Tab label="Manual" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      {pipelineSteps.map((step, index) => (
        <TabPanel key={step.key} value={tabValue} index={index}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={pipelineTablePaperSx}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    User
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    Contact
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    Loan Info
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    Approval Amount
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    Properties Added
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    Progress
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getUsersByPipelineStep(index).map((user: any) => (
                  <TableRow
                    key={user._id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                    onClick={() => handleUserClick(user)}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: theme.palette.mainColors.primary,
                            width: 40,
                            height: 40,
                          }}
                        >
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{ color: theme.palette.text.primary }}
                          >
                            {user.name || 'No Name'}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.primary }}
                      >
                        {user.email}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {user.phone || 'No Phone'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.primary }}
                        >
                          {userLoanEstimationTypeMap[
                            user.loanEstimationType as USER_LOAN_ESTIMATION_TYPE
                          ] || 'Not Set'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {userLoanProgramMap[
                            user.loanProgram as USER_LOAN_PROGRAM
                          ] || 'Not Set'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {userOccupancyMap[user.occupancy as USER_OCCUPANCY] ||
                            'Not Set'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={user.approvalAmount || ''}
                        onChange={(e) => {
                          const newValue = e.target.value
                            ? Number(e.target.value)
                            : undefined;
                          updateUserByAdmin({
                            id: user._id,
                            approvalAmount: newValue,
                          });
                        }}
                        size="small"
                        sx={{
                          width: 120,
                          '& .MuiOutlinedInput-root': {
                            color: theme.palette.text.primary,
                          },
                          ...outlinedControlSx,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.primary }}
                      >
                        {user.propertyCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={user.qualifyStatus || 'Not Started'}
                          color={
                            getStatusColor(user.qualifyStatus || '') as any
                          }
                          size="small"
                        />
                        <Select
                          value={
                            user.approveStatus ||
                            USER_APPROVE_STATUS.NOT_SUBMITTED
                          }
                          onChange={(e: any) => {
                            updateUserByAdmin({
                              id: user._id,
                              approveStatus: e.target.value,
                            });
                          }}
                          size="small"
                          sx={{
                            minWidth: 120,
                            '& .MuiSelect-select': {
                              color: theme.palette.text.primary,
                              fontSize: '0.75rem',
                              py: 0.5,
                            },
                            ...outlinedControlSx,
                          }}
                        >
                          {userApproveStatuses.map((status) => (
                            <MenuItem key={status.value} value={status.value}>
                              {status.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: '100%', maxWidth: 200 }}>
                        <LinearProgress
                          variant="determinate"
                          value={
                            (getPipelineStep(user) /
                              (pipelineSteps.length - 1)) *
                            100
                          }
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.text.primary, 0.08),
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          Step {getPipelineStep(user) + 1} of{' '}
                          {pipelineSteps.length}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          sx={{ color: theme.palette.mainColors.primary }}
                        >
                          <i className="fa-light fa-eye" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Request Documents">
                        <IconButton
                          size="small"
                          sx={{ color: theme.palette.mainColors.primary }}
                        >
                          <i className="fa-light fa-file-upload" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      ))}
      <TabPanel value={tabValue} index={manualTabIndex}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={pipelineTablePaperSx}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                  }}
                >
                  Buyer
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                  }}
                >
                  Manual Stage
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user: any) => (
                <TableRow key={user._id}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {user.name || 'Buyer'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {user.email || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={user.manualPipelineStage || ''}
                      onChange={(e) =>
                        handleManualStageChange(
                          user._id,
                          String(e.target.value)
                        )
                      }
                      displayEmpty
                      sx={{
                        minWidth: 220,
                        '& .MuiSelect-select': {
                          color: theme.palette.text.primary,
                        },
                        ...outlinedControlSx,
                      }}
                    >
                      <MenuItem value="">Auto</MenuItem>
                      {pipelineSteps.map((step) => (
                        <MenuItem key={step.key} value={step.key}>
                          {step.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredUsers.length && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Alert severity="info">No buyers found.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* User Detail Dialog */}
      <Dialog
        open={userDetailDialog}
        onClose={() => setUserDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: theme.palette.text.primary }}>
          User Details - {selectedUser?.name}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  sx={{ color: theme.palette.text.primary, mb: 2 }}
                >
                  Personal Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Name:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {selectedUser.name || 'Not Provided'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Email:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {selectedUser.email}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Phone:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {selectedUser.phone || 'Not Provided'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  sx={{ color: theme.palette.text.primary, mb: 2 }}
                >
                  Loan Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Monthly Income:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      ${selectedUser.loanInfo?.monthlyIncome || 'Not Provided'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Credit Score:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {selectedUser.loanInfo?.creditScore || 'Not Provided'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Down Payment:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      ${selectedUser.loanInfo?.downPayment || 'Not Provided'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ color: theme.palette.text.primary, mb: 2 }}
                >
                  Application Progress
                </Typography>
                <Stepper
                  orientation="horizontal"
                  activeStep={getPipelineStep(selectedUser)}
                >
                  {pipelineSteps.map((step) => (
                    <Step key={step.key}>
                      <StepLabel
                        sx={{
                          '& .MuiStepLabel-label': {
                            color: theme.palette.text.secondary,
                            fontSize: '0.8rem',
                          },
                          '& .MuiStepLabel-label.Mui-active': {
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                          },
                          '& .MuiStepLabel-label.Mui-completed': {
                            color: theme.palette.text.primary,
                          },
                        }}
                      >
                        {step.label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setUserDetailDialog(false)}
            sx={{ borderColor: theme.palette.greyShades.border1 }}
          >
            Close
          </Button>
          <Button variant="contained" color="primary">
            Request Documents
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
